// Pure derivation of a single window's nav view from its stored position + the
// fetched item tree. No React — `useCodeWorkspace` runs this for the active
// window and exposes the result to the nav list + breadcrumb.
//
// A window's stored position (`nav`) is serializable:
//   { standard, trailOrders, collapsed, mode, ... }
// where `trailOrders` are the drilled nodes BELOW the standard root (root is
// implicit), and `collapsed` are the `order`s of folded subtrees (collapsed
// view only).
//
// The item `level` + `path` (ancestor `order`s) define the tree: a node N's
// immediate children are items with `level === N.level + 1` whose `path`
// contains `N.order`; its descendants are every item whose `path` contains
// `N.order`.
//
// When a filter is active (matchers.active) inside a standard: segmented keeps
// children that match or have a matching descendant; collapsed keeps matches
// plus their ancestor chain (ancestors flagged dimmed). A folded subtree stays
// folded either way, so the chevron still hides that section's descendants.
// The root list keeps matching standards.

// Map a PDF page to the section the reader is in and return the trail from the
// standard root down to that section (root first, current section last). The
// current section is the heading that started most recently at or before `page`
// and has not ended yet. Recency is pageStart, not outline order: a later
// bookmark can point at an earlier page (ASCE Chapter 12 is outlined after
// Chapter 11 but its bookmark lands before it), and picking by order then
// shows that later chapter while the earlier one is still on screen.
// Ties (several headings that begin on the same page) go to the later outline
// entry, which is the more specific child. Falls back to the root when no
// section has started yet.
export function locatePath(items, page) {
  if (!items?.length) return []
  const byOrder = new Map(items.map((it) => [it.order, it]))
  const root = items.find((it) => it.level === 0) || null

  let current = null
  if (page) {
    for (const it of items) {
      if (it.level <= 0 || !Number.isFinite(it.pageStart) || it.pageStart > page) continue
      if (Number.isFinite(it.pageEnd) && it.pageEnd < page) continue
      const laterStart = current && it.pageStart > current.pageStart
      const samePageMoreSpecific =
        current && it.pageStart === current.pageStart && it.order > current.order
      if (!current || laterStart || samePageMoreSpecific) current = it
    }
  }
  if (!current) return root ? [root] : []

  const ancestors = (current.path || []).map((o) => byOrder.get(o)).filter(Boolean)
  const hasRoot = ancestors.some((a) => a.level === 0)
  const trail = hasRoot || !root ? ancestors : [root, ...ancestors]
  return [...trail, current]
}

export function deriveView({ nav, items, standards, matchers }) {
  const atRoot = nav.standard == null
  const mode = nav.mode === 'collapsed' ? 'collapsed' : 'segmented'
  const filterActive = Boolean(matchers?.active)

  // Every `order` that appears in some item's path is a parent (has children).
  const parentOrders = new Set()
  items.forEach((it) => (it.path || []).forEach((o) => parentOrders.add(o)))

  const byOrder = new Map(items.map((it) => [it.order, it]))
  const root = items.find((it) => it.level === 0) || null
  const drilled = (nav.trailOrders || []).map((o) => byOrder.get(o)).filter(Boolean)
  const trail = root ? [root, ...drilled] : []
  const currentNode = trail.length ? trail[trail.length - 1] : null

  const collapsedSet = new Set(nav.collapsed || [])

  const children = currentNode
    ? items
        .filter(
          (it) =>
            it.level === currentNode.level + 1 && (it.path || []).includes(currentNode.order)
        )
        .sort((a, b) => a.order - b.order)
    : []

  const descendants = currentNode
    ? items
        .filter((it) => it._id !== currentNode._id && (it.path || []).includes(currentNode.order))
        .sort((a, b) => a.order - b.order)
    : []

  // A row sits inside a folded subtree when one of its ancestors was collapsed.
  const outsideCollapsed = (it) => !(it.path || []).some((o) => collapsedSet.has(o))

  // Matching set (+ ancestor path orders) while filtering inside a standard.
  let matchSet = null
  let matchPathOrders = null
  if (filterActive && !atRoot && matchers.matchesItem) {
    matchSet = new Set()
    matchPathOrders = new Set()
    items.forEach((it) => {
      if (matchers.matchesItem(it)) {
        matchSet.add(it._id)
        ;(it.path || []).forEach((o) => matchPathOrders.add(o))
      }
    })
  }

  const keepRow = (item) => matchSet.has(item._id) || matchPathOrders.has(item.order)

  let rows
  if (atRoot) {
    rows = filterActive && matchers.matchesStandard ? standards.filter(matchers.matchesStandard) : standards
  } else if (mode === 'collapsed') {
    const base = !filterActive || !matchSet ? descendants : descendants.filter(keepRow)
    rows = base.filter(outsideCollapsed)
  } else {
    rows = !filterActive || !matchSet ? children : children.filter(keepRow)
  }

  const matchCount = !filterActive
    ? 0
    : atRoot
      ? matchers.matchesStandard
        ? standards.filter(matchers.matchesStandard).length
        : 0
      : matchSet
        ? descendants.filter((it) => matchSet.has(it._id)).length
        : 0

  return {
    atRoot,
    mode,
    trail,
    rows,
    currentNode,
    parentOrders,
    collapsedSet,
    matchSet,
    matchCount,
  }
}
